<script>
import { mapState, mapActions } from 'vuex'
import AttachmentsView from '@/views/components/AttachmentsView'
import DOMPurify from 'dompurify'

export default {
  name: 'PostView',
  components: { AttachmentsView },
  props: {
    post: { type: Object, default: null }
  },
  data: () => ({
    unsubscribe: null,
    // post: null,
    winRatio: window.innerWidth / window.innerHeight,
    image: null,
    imgLoadedClass: null,
    boxSizeClass: '',
    showMore: false,
    paused: false,
    muted: true,
    progress: 0,
    timeupdate: null
  }),

  computed: {
    ...mapState(['user', 'feed']),
    allowEdit () {
      return !!this.user && (this.user.role === 'admin' || this.user.role === 'editor')
    },

    postId () {
      return this.$route.params.id || this.post.id
    },

    shortDesc () {
      const tech = (this.post || {}).technique || ''
      const year = (this.post || {}).year || ''
      return `${tech}${tech && year ? ' | ' : ''}${year}`
    },

    title () {
      return (this.post || {}).title || ''
    },
    credits () {
      return (this.post || {}).credits || ''
    },

    content () {
      if ((this.post || {}).content) {
        return DOMPurify.sanitize(this.post.content)
      }
      return ''
    },

    orderedAttachments () {
      return (Object.values((this.post || {}).attachments || {}) || []).sort((a, b) => a.order - b.order)
    },

    otherAttachments () {
      if (this.$route.name === 'cv') {
        return this.orderedAttachments.filter(item => item.type !== 'embed/vimeo')
      }
      const [, ...rest] = this.orderedAttachments
      return rest
    },

    firstAttachment () {
      if (this.$route.name === 'cv') {
        return this.orderedAttachments.find(item => item.type === 'embed/vimeo') || null
      }
      return this.orderedAttachments[0] || null
    },

    firstAttachmentIsVideo () {
      return (this.firstAttachment || {}).type === 'embed/vimeo'
    },

    firstDimensions () {
      const { full, original } = (this.firstAttachment || {}).srcSet || {}
      return (full || {}).dimensions || (original || {}).dimensions
    },

    orientation () {
      if (!this.firstDimensions) return null
      const { w, h } = this.firstDimensions
      return w > h ? 'landscape' : 'portrait'
    },

    videoPlaybackCountDown () {
      if (!this.timeupdate) return ''
      const toTimeCode = timeSec => {
        const sec = parseInt(Math.abs(timeSec), 10) // don't forget the second param
        let hours = Math.floor(sec / 3600)
        let minutes = Math.floor((sec - (hours * 3600)) / 60)
        let seconds = sec - (hours * 3600) - (minutes * 60)

        if (hours > 0 && hours < 10) { hours = '0' + hours }
        if (minutes < 10) { minutes = '0' + minutes }
        if (seconds < 10) { seconds = '0' + seconds }
        return (hours > 0 ? `${hours}:` : '') + minutes + ':' + seconds
      }
      const remained = this.timeupdate.seconds - this.timeupdate.duration
      return (remained < 0 ? '-' : '') + toTimeCode(remained)
    },

    downloadLink () {
      const pdf = this.orderedAttachments.find(item => item.type === 'application/pdf')
      if (!pdf) return ''
      const srcSet = pdf.srcSet || {}
      const file = srcSet.original || srcSet.full || srcSet.preview
      return (file || {}).url || ''
    },

    showCvDownload () {
      return this.$route.name === 'cv' && (!!this.downloadLink || !!this.content)
    }
  },

  mounted () {
    this.emitAllowEdit()
    this.emitDownloadLink()
  },

  watch: {
    $route () {
      this.imgLoadedClass = null
      this.jumpToTop()
    },

    firstDimensions () {
      this.setBoxSizeCss()
      this.$emit('update-box')
    },
    winRatio () {
      this.setBoxSizeCss()
      this.$emit('update-box')
    },

    allowEdit () {
      this.emitAllowEdit()
    },

    downloadLink () {
      this.emitDownloadLink()
    },

    post () {
      this.$emit('set-post', this.post)
      this.emitDownloadLink()
    }
  },

  methods: {
    ...mapActions(['showEditor']),
    jumpToTop () {
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      })
    },
    openEditor () {
      if (this.post) this.showEditor({ value: this.post })
    },

    downloadCv (event) {
      if (this.downloadLink) return

      event.preventDefault()
      const printWindow = window.open('', '_blank')
      if (!printWindow) return

      printWindow.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Aleksander Komarov — CV</title>
  <style>
    body {
      font-family: Georgia, "Times New Roman", serif;
      font-size: 12pt;
      line-height: 1.45;
      color: #111;
      max-width: 42rem;
      margin: 2rem auto;
      padding: 0 1.25rem;
    }
    h1 { font-size: 1.5rem; margin: 0 0 1.5rem; font-weight: normal; }
    p { margin: 0 0 0.85rem; }
    ul, ol { margin: 0 0 0.85rem; padding-left: 1.25rem; }
    a { color: inherit; }
    @media print {
      body { margin: 0; max-width: none; }
    }
  </style>
</head>
<body>
  <h1>Aleksander Komarov — CV</h1>
  ${this.content || ''}
</body>
</html>`)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 250)
    },

    async setBoxSizeCss () {
      /** @type HTMLElement */
      const box = this.$refs.box
      if (!box) return
      if (!this.firstDimensions) return

      const root = document.documentElement
      if (!this.rootCompStyle) this.rootCompStyle = getComputedStyle(root)
      if (!this.boxCompStyle) this.boxCompStyle = getComputedStyle(box)

      const rootFontSize = parseFloat(this.rootCompStyle.fontSize)
      const footerH = (parseFloat(this.boxCompStyle.getPropertyValue('--footer-height')) || 1) * rootFontSize
      const maxH = (parseFloat(this.boxCompStyle.getPropertyValue('--max-box-height')) || 1)
      const boxMarg = (parseFloat(this.boxCompStyle.getPropertyValue('--box-margins')) || 1) * rootFontSize

      const { w, h } = this.firstDimensions
      const attachmentRatio = w / h

      const testH = Math.min(window.innerHeight - boxMarg * 2 - footerH, maxH)

      const testW = testH * attachmentRatio
      const testHWithFooter = testH + footerH
      const boxWithFooterRatio = testW / testHWithFooter
      if (boxWithFooterRatio >= this.winRatio) {
        this.boxSizeClass = 'fit-in-width'
      } else {
        this.boxSizeClass = 'fit-in-height'
      }
      box.style.setProperty('--box-ratio', `${attachmentRatio}`)
      root.style.setProperty('--modal-box-ratio', `${attachmentRatio}`)
      root.style.setProperty('--modal-box-fit-type', `${this.boxSizeClass}`)
    },

    openMore () {
      this.showMore = !this.showMore
      this.pausedBeforeMore = this.paused
      if (this.paused) return
      this.toggleVimeoPlay(false)
    },

    closeMore () {
      this.showMore = false
      if (this.pausedBeforeMore === false) {
        this.toggleVimeoPlay(true)
      }
    },

    emitAllowEdit () {
      this.$emit('allow-edit', this.allowEdit)
    },

    emitDownloadLink () {
      this.$emit('download-link', this.downloadLink)
    }
  }
}
</script>

<template>
  <div ref="box" class="post-view relative">
    <div class="first-attachment-container">
      <attachments-view v-if="firstAttachment" ref="first-attachment" :attachments="[firstAttachment]"
        class="first-attachment" @paused="paused = $event" @muted="muted = $event" @timeupdate="timeupdate = $event"
        @progress="progress = $event" />
      <div v-else class="first-attachment" />
    </div>
    <div ref="container" class="content">
      <attachments-view v-if="otherAttachments.length" :attachments="otherAttachments" resize-boxes />
      <div class="txt-block-container">
        <p v-if="credits" class="txt-block credits">{{ credits }}</p>
        <a
          v-if="showCvDownload"
          class="cv-download"
          :href="downloadLink || '#'"
          :download="downloadLink ? 'Aleksander-Komarov-CV.pdf' : null"
          :target="downloadLink ? '_blank' : null"
          :rel="downloadLink ? 'noopener noreferrer' : null"
          @click="downloadCv">
          Download CV
        </a>
        <div v-if="content" v-html="content" class="txt-block" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.post-view {
  --caption-padding: 0;
  --block-margin: var(--base-padding);
  --block-margin: clamp(var(--base-padding), 4vw, var(--base-size) * 0.5);
  --inline-margin: var(--small-padding);
  --inline-margin: clamp(var(--small-padding), 3vw, var(--base-size));
  background: transparent;
  min-height: 100vh;
  min-height: var(--win-height, 100vh);
  min-height: 100dvh;
  width: 100vw;
  padding-top: var(--base-size);
}

.first-attachment-container {
  --caption-padding: var(--small-padding);
  position: relative;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

.content {
  /* width: min(960px, 100vw - 2 * var(--small-padding)); */
  width: calc(100vw - 2 * var(--inline-margin));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 200px 1fr;
  grid-template-columns: clamp(200px, 40%, 75ch) 1fr;
  padding-bottom: var(--footer-height);
  grid-gap: var(--inline-margin);
  gap: var(--inline-margin);

  &>* {
    grid-row: 1;
    height: fit-content;
    position: sticky;
    top: var(--base-size);
  }

  .txt-block-container {
    grid-column: 1;
  }

  .cv-download {
    display: inline-block;
    margin-bottom: var(--block-margin);
    padding-left: var(--small-padding);
    padding-right: var(--small-padding);
    text-decoration: underline;
    text-underline-offset: 0.2em;
  }

  .attachments-view {
    grid-column: 2;
    display: grid;
    grid-template-columns: var(--inline-margin) 1fr var(--inline-margin);

    .attachment {
      margin-bottom: var(--block-margin);
    }

    &> :nth-child(even) {
      grid-column: 1 / 3;
    }

    &> :nth-child(odd) {
      grid-column: 2 / 4;
    }
  }

  .txt-block {
    padding-left: var(--small-padding);
    padding-right: var(--small-padding);
    word-break: break-word;

    &:not(:last-child) {
      margin-bottom: var(--block-margin);
    }

    &.credits {
      margin-bottom: var(--base-padding);
      margin-left: auto;
      width: fit-content;
    }
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0;

    &>* {
      grid-row: auto;
      position: initial;
      top: initial;
    }

    .attachments-view {
      grid-column: 1;
    }
  }
}

.event {
  .list-string {
    &>*:not(:last-child) {
      &:after {
        content: ", ";
      }
    }
  }
}
</style>
<style scoped>
.more-popup {
  position: fixed;
  overflow: auto;
  z-index: 2000000;
}
</style>
